import { Box, Typography, Grid, LinearProgress, Link } from "@mui/material"
import { styled } from "@mui/material/styles"
import { EFidelityLevelMan, EFidelityLevelWoman, type FidelityLevel, Gender } from "../../hooks/auth/models"
import { GREEN_LOGO, GREEN_MAIN } from "../../theme/colors"
import { useTranslationContext } from "../../hooks/translations/translationsProvider"
import settings from "../../utilities/settings"

// Styled components
const ProgressBarContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  height: 2,
  marginBottom: theme.spacing(2),
}))

const ProgressIndicator = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: 0,
  height: 0,
  borderLeft: "9px solid transparent",
  borderRight: "9px solid transparent",
  borderBottom: `10px solid ${theme.palette.error.main}`,
  top: -11,
  transform: "translateX(-50%)",
  zIndex: 1,
}))

const ImageContainer = styled(Box)<{ active: boolean }>(({ active }) => ({
  opacity: active ? 1 : 0.15,
  transition: "opacity 0.3s ease",
}))

export default function FidelityLevelSelector({ level, gender }: FidelityLevel) {
  const {t} = useTranslationContext();
  type LevelType = EFidelityLevelMan[] | EFidelityLevelWoman[]
  const levels: LevelType =
    gender === Gender.male
      ? [EFidelityLevelMan.PIONEER, EFidelityLevelMan.LORD, EFidelityLevelMan.KING]
      : [EFidelityLevelWoman.PIONEER, EFidelityLevelWoman.LADY, EFidelityLevelWoman.QUEEN]

  // Map levels to their image paths
  const getLevelImage = (lvl: string, gender: Gender.male | Gender.female): string => {
    if (lvl === EFidelityLevelMan.PIONEER || lvl === EFidelityLevelWoman.PIONEER) {
      return "/01-pioneer.png"
    }

    if (gender === Gender.male) {
      switch (lvl) {
        case EFidelityLevelMan.LORD:
          return "/02-lord.png"
        case EFidelityLevelMan.KING:
          return "/03-king.png"
        default:
          return "/01-pioneer.png"
      }
    } else {
      switch (lvl) {
        case EFidelityLevelWoman.LADY:
          return "/02-lady.png"
        case EFidelityLevelWoman.QUEEN:
          return "/03-queen.png"
        default:
          return "/01-pioneer.png"
      }
    }
  }

  // Get the index of the current level
  let currentLevelIndex = 0
  if (gender === Gender.male) {
    if (level === EFidelityLevelMan.LORD) currentLevelIndex = 1
    else if (level === EFidelityLevelMan.KING) currentLevelIndex = 2
  } else {
    if (level === EFidelityLevelWoman.LADY) currentLevelIndex = 1
    else if (level === EFidelityLevelWoman.QUEEN) currentLevelIndex = 2
  }

  // Points thresholds for each level
  const pointThresholds = [
    { min: 0, max: 600 },
    { min: 601, max: 1000 },
    { min: 1001, max: Number.POSITIVE_INFINITY },
  ]

  // Calculate progress percentage
  // const progressPercentage = (currentLevelIndex / 2) * 100
  let progressPercentage: number;

  switch (gender) {
    case Gender.male:
      switch (currentLevelIndex) {
        case 0: progressPercentage = 16; break; // PIONEER
        case 1: progressPercentage = 50; break; // LORD
        case 2: progressPercentage = 84; break; // KING
        default: progressPercentage = 0;
      }
      break;

    case Gender.female:
      switch (currentLevelIndex) {
        case 0: progressPercentage = 16; break; // PIONEER
        case 1: progressPercentage = 50; break; // LADY
        case 2: progressPercentage = 84; break; // QUEEN
        default: progressPercentage = 0;
      }
      break;

    default:
      progressPercentage = 0;
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 2 }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        
        <Typography variant="h4" sx={{ color: GREEN_MAIN, fontWeight: "bold" }}>
          Member fidelity profile
        </Typography>
        {/*<Typography variant="body1">E&apos;</Typography>*/}
      </Box>

      {/* Level images */}
      <Grid container spacing={2} sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
        {levels.map((lvl, index) => (
          <Grid item xs={4} key={index} sx={{ display: "flex", justifyContent: "center" }}>
            <ImageContainer active={level === lvl}>
              <Box
                component="img"
                src={getLevelImage(lvl, gender) || "/placeholder.svg"}
                alt={lvl}
                sx={{
                  width: 120, //lvl.includes("KING") || lvl.includes("QUEEN") ? 150 : 120,
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </ImageContainer>
          </Grid>
        ))}
      </Grid>

      {/* Progress bar */}
      <ProgressBarContainer>
        <LinearProgress
          variant="determinate"
          value={100} // {progressPercentage}
          sx={{
            height: 2,
            backgroundColor: "#e0e0e0",
            "& .MuiLinearProgress-bar": {
              backgroundColor: `${GREEN_LOGO} !important`,
            },
          }}
        />
        <ProgressIndicator
          sx={{
            left: `${progressPercentage}%`,
          }}
        />
      </ProgressBarContainer>

      {/* Point thresholds */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {pointThresholds.map((threshold, index) => (
          <Grid item xs={4} key={index}>
            <Box
              sx={{
                textAlign: "center",
                ...(index === currentLevelIndex ? {
                  fontWeight: "bold",
                  color: GREEN_MAIN,
                }:{
                  color: `${GREEN_MAIN}40`,
                }),
              }}
            >
              <Typography variant="caption" sx={{ display: "block", fontSize:'14px' }}>
                {threshold.min} - {threshold.max !== Number.POSITIVE_INFINITY ? threshold.max : "1000+"} {t("punti fedeltà")}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", fontSize:'14px'  }}>
                {t("negli ultimi 6 mesi")}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Call to action */}
      <Box sx={{ textAlign: "center", color: GREEN_MAIN }}>
        <Typography>
          {t("Ottieni altri Punti fedeltà.")} {" "}
          <Link href={`${settings.InfosWebsite.Url}/tariffe`} target="_blank" rel="noopener noreferrer" underline="none" sx={{ color: "inherit" }}>
            {t("Clicca qui per scoprire come")}
          </Link>
        </Typography>
      </Box>
    </Box>
  )
}
